import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { getEstoreDashboard } from "store/actions/dashboardAction"
import SalesByStatuses from "./SalesByStatuses"
import SalesByProducts from "./SalesByProducts"
import SalesBySLA from "./SalesBySLA"
import { useTranslation } from "react-i18next"
import { formatWithCommas } from "common/utils"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Card, CardBody, Col, Row, CardTitle } from "reactstrap"

const EStore = ({ commonDateRange, periodType, date, hasEstorePermission }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { loading, estoreDashboardData } = useSelector(state => state.Dashboard)

  const formatDate = date => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const day = date.getDate().toString().padStart(2, "0")
    const hours = date?.getHours()?.toString()?.padStart(2, "0")
    const minutes = date?.getMinutes()?.toString()?.padStart(2, "0")
    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  const generateDateRange = () => {
    if (periodType === "day") {
      return {
        start: formatDate(new Date(date)).split(" ")[0].concat(" 00:00"),
        end: formatDate(new Date(date)).split(" ")[0].concat(" 23:59"),
      }
    }

    if (periodType === "custom") {
      return {
        start: formatDate(new Date(commonDateRange?.start))
          .split(" ")[0]
          .concat(" 00:00"),
        end: formatDate(new Date(commonDateRange?.end))
          .split(" ")[0]
          .concat(" 23:59"),
      }
    }

    return commonDateRange
  }

  const fetchEstoreDashboardData = () => {
    // Only fetch if user has E-Store permission
    if (!hasEstorePermission) return
    
    console.log("date", generateDateRange())

    const payload = {
      dateRange: generateDateRange() || {},
    }

    dispatch(getEstoreDashboard(payload))
  }

  useEffect(() => {
    fetchEstoreDashboardData()
  }, [commonDateRange, periodType, date])

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: context =>
            `${context.dataset.label}: ${formatWithCommas(context.raw)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  const pieOptions = {
    plugins: {
      legend: {
        display: true,
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            // Access the dataset and data
            const dataset = tooltipItem?.dataset
            const count = dataset?.counts[tooltipItem?.dataIndex] // Access the count from the custom property

            // Return the custom tooltip
            return [`${t("Total Orders")}: ${formatWithCommas(count)}`]
          },
        },
      },
    },
  }

  const getTotal = (data, field) => {
    return data?.reduce((sum, item) => sum + (item?.[field] || 0), 0)
  }

  const estoreStats = [
    {
      id: 1,
      title: t("Total Revenue"),
      value: formatWithCommas(
        estoreDashboardData?.statusTotals?.totalAmount || 0
      ),
    },
    {
      id: 2,
      title: t("Total Orders"),
      value: formatWithCommas(
        estoreDashboardData?.statusTotals?.totalOrders || 0
      ),
    },
    {
      id: 3,
      title: t("Total Product Quantities"),
      value: formatWithCommas(
        estoreDashboardData?.productsTotals?.totalQuantity || 0
      ),
    },
  ]

  if (loading) {
    return (
      <>
        <Row>
          {estoreStats?.map(item => (
            <Col key={item.id} lg={4}>
              <Card className="card_100_24">
                <CardBody>
                  <CardTitle className="text-center">{item.title}</CardTitle>
                  <div style={{ direction: "ltr" }}>
                    <ShimmerThumbnail
                      height={30}
                      rounded
                      className="mb-0 pb-0"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <Row key={i}>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <ShimmerThumbnail height={400} rounded />
                </CardBody>
              </Card>
            </Col>
          </Row>
        ))}
      </>
    )
  }

  return (
    <>
      <Row>
        {estoreStats?.map(item => (
          <Col key={item.id} lg={4}>
            <Card className="card_100_24">
              <CardBody>
                <CardTitle className="text-center">{item.title}</CardTitle>
                <div style={{ direction: "ltr" }}>
                  {loading ? (
                    <ShimmerThumbnail
                      height={30}
                      rounded
                      className="mb-0 pb-0"
                    />
                  ) : (
                    <h4 className="text-center">{item.value}</h4>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
      <SalesByStatuses
        getTotal={getTotal}
        barOptions={barOptions}
        estoreDashboardData={estoreDashboardData}
      />

      <SalesByProducts
        getTotal={getTotal}
        barOptions={barOptions}
        estoreDashboardData={estoreDashboardData}
      />

      <SalesBySLA
        getTotal={getTotal}
        pieOptions={pieOptions}
        estoreDashboardData={estoreDashboardData}
      />
    </>
  )
}

export default EStore
